/**
 * Colour tokens for consumers that cannot resolve CSS custom properties.
 *
 * `color.css` is the single source of truth. This file exists only because
 * two consumers need a resolved value at call time:
 *
 *   • `<canvas>` — `ctx.fillStyle = 'var(--x)'` is invalid; canvas has no
 *     cascade, so it needs a literal.
 *   • Library props typed as `string` that are handed to a non-DOM renderer.
 *
 * Anything that renders to the DOM should use `var(--token)` or the Tailwind
 * utility instead, so it follows the active theme automatically.
 *
 * ── Keeping this in sync ────────────────────────────────────────────────
 * Values here MUST match the light-theme block of `color.css`. Prefer
 * `readToken()` where a live read is possible; the literals below are the
 * SSR/canvas fallback.
 */

/** Read a token's computed value at runtime. Falls back for SSR/tests. */
export function readToken(name: string, fallback = '#000000'): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v || fallback;
}

/* ── Clinical: odontogram condition scale ──────────────────────────────
   SEPARATE NAMESPACE. Never reuse one of these for UI state.
   Light-theme literals; `readToken` picks up the dark values.
   ─────────────────────────────────────────────────────────────────── */
export const TOOTH = {
  caries: '#e0483c',
  cariesDeep: '#a7251a',
  pulpitisReversible: '#ed968f',
  pulpitisChronic: '#e77168',
  pulpitisAcute: '#dd3628',
  pulpitisIrreversible: '#ce2d21',
  necrotic: '#7f1c14',
  abscess: '#932017',
  periapical: '#ba291e',
  periodontitis: '#d83022',
  gingivitis: '#ea847c',
  recession: '#e45f55',
  mobility: '#f0a9a3',
  fractured: '#b0271c',

  filling: '#2d8fbf',
  inlay: '#44a4d3',
  onlay: '#2678a0',
  veneer: '#5eb1d9',
  sealant: '#75bcdf',
  hypersensitivity: '#53abd6',
  spaceMaintainer: '#206486',

  crown: '#d9a02b',
  bridgeAbutment: '#be8b22',
  bridgePontic: '#9a711c',
  erosion: '#e1b457',

  rootCanal: '#b0459b',
  postAndCore: '#90397f',
  orthoBracket: '#c467b1',
  impacted: '#bd56a8',
  unerupted: '#cd80be',

  implant: '#66707f',
  extraction: '#525a66',
  attrition: '#828c9a',
  abrasion: '#919aa7',
  discoloration: '#727d8e',

  missing: '#d2d4d9',

  body: '#ffffff',
  outline: '#b9bcc5',
  selected: '#e7e8fb',
  selectedOutline: '#4a52c4',
  label: '#14151a',
} as const;

/** Live-reading variants for canvas, which repaints on theme change. */
export const toothToken = (key: keyof typeof TOOTH): string =>
  readToken(`--tooth-${camelToKebab(key)}`, TOOTH[key]);

function camelToKebab(s: string): string {
  return s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}

/* ── Categorical: chart series ────────────────────────────────────────
   Ordered so adjacent entries stay separable for common CVD types;
   blue and violet are never neighbours.
   ─────────────────────────────────────────────────────────────────── */
export const CHART_SERIES = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
  'var(--chart-8)',
] as const;

/** Resolved equivalents, for canvas-based charts. */
export const CHART_SERIES_HEX = [
  '#4a52c4',
  '#0fa391',
  '#ef6b3c',
  '#8a90e8',
  '#c98a0a',
  '#0c8274',
  '#b0459b',
  '#5f626d',
] as const;
